import { useState, useCallback } from 'react';
import { Clip } from '@/types/clip.types';
import { MergePreview } from '@/types/episode.types';
import { AudioPlayerHandle } from '@/components/AudioPlayer';
import { buildMergeSequence } from '@/utils/episode-helpers';

interface UsePlaybackControllerArgs {
  clips: Clip[];
  mergePreview: MergePreview | undefined;
  mergedAudioRef: React.RefObject<AudioPlayerHandle | null>;
}

export function usePlaybackController({ clips, mergePreview, mergedAudioRef }: UsePlaybackControllerArgs) {
  // Play All state for sequential playback (merge sequence with intro/outro/commercials)
  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const [playAllIndex, setPlayAllIndex] = useState<number | null>(null);
  const [isPlayAllPaused, setIsPlayAllPaused] = useState(false);

  // Play All Clips state for clips-only sequential playback (audio review)
  const [isPlayingAllClips, setIsPlayingAllClips] = useState(false);
  const [playAllClipsIndex, setPlayAllClipsIndex] = useState<number | null>(null);
  const [isPlayAllClipsPaused, setIsPlayAllClipsPaused] = useState(false);

  // Single audio playback coordination - only one audio plays at a time
  const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);

  // Merged episode audio player state
  const [isPlayingMerged, setIsPlayingMerged] = useState(false);

  // ── Play All (merge sequence) ──────────────────────────────

  const handlePlayAll = useCallback(() => {
    setIsPlayingAll(true);
    setIsPlayAllPaused(false);
    setPlayAllIndex(0);
  }, []);

  const handleStopPlayAll = useCallback(() => {
    setIsPlayingAll(false);
    setIsPlayAllPaused(false);
    setPlayAllIndex(null);
  }, []);

  const handlePausePlayAll = useCallback(() => {
    setIsPlayAllPaused(true);
  }, []);

  const handleResumePlayAll = useCallback(() => {
    setIsPlayAllPaused(false);
  }, []);

  const handleNextSegment = useCallback(() => {
    if (!mergePreview) return;
    const sequence = buildMergeSequence(clips, mergePreview);
    if (playAllIndex !== null && playAllIndex < sequence.length - 1) {
      setPlayAllIndex(playAllIndex + 1);
      setIsPlayAllPaused(false);
    } else {
      setIsPlayingAll(false);
      setIsPlayAllPaused(false);
      setPlayAllIndex(null);
    }
  }, [clips, mergePreview, playAllIndex]);

  const handleSegmentEnded = useCallback((sequenceIndex: number) => {
    if (!mergePreview) return;
    const sequence = buildMergeSequence(clips, mergePreview);
    if (isPlayingAll && sequenceIndex === playAllIndex) {
      if (playAllIndex < sequence.length - 1) {
        setPlayAllIndex(playAllIndex + 1);
      } else {
        setIsPlayingAll(false);
        setPlayAllIndex(null);
      }
    }
  }, [clips, mergePreview, isPlayingAll, playAllIndex]);

  // ── Play All Clips (clips only) ───────────────────────────

  const handlePlayAllClips = useCallback(() => {
    if (clips.length === 0) return;
    setIsPlayingAllClips(true);
    setIsPlayAllClipsPaused(false);
    setPlayAllClipsIndex(0);
    setCurrentlyPlayingId(clips[0].id);
  }, [clips]);

  const handleStopPlayAllClips = useCallback(() => {
    setIsPlayingAllClips(false);
    setIsPlayAllClipsPaused(false);
    setPlayAllClipsIndex(null);
    setCurrentlyPlayingId(null);
  }, []);

  const handlePausePlayAllClips = useCallback(() => {
    setIsPlayAllClipsPaused(true);
  }, []);

  const handleResumePlayAllClips = useCallback(() => {
    setIsPlayAllClipsPaused(false);
  }, []);

  const handleNextClip = useCallback(() => {
    if (playAllClipsIndex !== null && playAllClipsIndex < clips.length - 1) {
      const nextIndex = playAllClipsIndex + 1;
      setPlayAllClipsIndex(nextIndex);
      setIsPlayAllClipsPaused(false);
      setCurrentlyPlayingId(clips[nextIndex].id);
    } else {
      setIsPlayingAllClips(false);
      setIsPlayAllClipsPaused(false);
      setPlayAllClipsIndex(null);
      setCurrentlyPlayingId(null);
    }
  }, [clips, playAllClipsIndex]);

  const handleClipEnded = useCallback((clipIndex: number) => {
    if (isPlayingAllClips && clipIndex === playAllClipsIndex) {
      if (playAllClipsIndex !== null && playAllClipsIndex < clips.length - 1) {
        const nextIndex = playAllClipsIndex + 1;
        setPlayAllClipsIndex(nextIndex);
        setCurrentlyPlayingId(clips[nextIndex].id);
      } else {
        setIsPlayingAllClips(false);
        setPlayAllClipsIndex(null);
        setCurrentlyPlayingId(null);
      }
    }
  }, [clips, isPlayingAllClips, playAllClipsIndex]);

  // ── Merged audio playback ─────────────────────────────────

  const handlePlayMerged = useCallback(() => {
    mergedAudioRef.current?.play();
    setIsPlayingMerged(true);
  }, [mergedAudioRef]);

  const handlePauseMerged = useCallback(() => {
    mergedAudioRef.current?.pause();
    setIsPlayingMerged(false);
  }, [mergedAudioRef]);

  // ── Single playback coordination ──────────────────────────

  const handlePlayStateChange = useCallback((id: string, playing: boolean) => {
    if (playing) {
      setCurrentlyPlayingId(id);
      if (isPlayingAll) {
        setIsPlayingAll(false);
        setPlayAllIndex(null);
      }
      if (isPlayingAllClips) {
        setIsPlayingAllClips(false);
        setPlayAllClipsIndex(null);
      }
    } else if (currentlyPlayingId === id) {
      setCurrentlyPlayingId(null);
    }
  }, [isPlayingAll, isPlayingAllClips, currentlyPlayingId]);

  return {
    // Play All (merge sequence)
    isPlayingAll,
    playAllIndex,
    isPlayAllPaused,
    handlePlayAll,
    handleStopPlayAll,
    handlePausePlayAll,
    handleResumePlayAll,
    handleNextSegment,
    handleSegmentEnded,

    // Play All Clips
    isPlayingAllClips,
    playAllClipsIndex,
    isPlayAllClipsPaused,
    handlePlayAllClips,
    handleStopPlayAllClips,
    handlePausePlayAllClips,
    handleResumePlayAllClips,
    handleNextClip,
    handleClipEnded,

    // Single playback
    currentlyPlayingId,
    handlePlayStateChange,

    // Merged audio
    isPlayingMerged,
    setIsPlayingMerged,
    handlePlayMerged,
    handlePauseMerged,
  };
}
